import type { AccessNodePublic, ApplicationPublic } from '@accounts/shared';

import { STR } from '../../../../i18n';
import {
  checkboxRowStyle,
  checkboxStyle,
  fieldLabelStyle,
  hintStyle,
  textFieldStyle,
} from '../../../../ui/styles';

import { FiledUnderPicker } from './FiledUnderPicker';

/** The editable half of a catalog row — what the add dialog and the drawer both edit. */
export interface RowDraft {
  name: string;
  nodeId: string | null;
  applicationId: string | null;
  requestable: boolean;
  hrCanGrant: boolean;
}

/**
 * The row's settings, in the order the FINAL demo lists them: Filed under, Row name,
 * Connected app, Requestable, Approver, HR can grant directly. One component for the add
 * dialog and the drawer, so the two can never describe a field differently.
 *
 * Approver is shown and inert: administrators are the only approver the server knows
 * today (`approverKind: 'admin'`); the column is ready for a person or a role later.
 */
export function RowFields({
  draft,
  onChange,
  nodes,
  applications,
  disabled = false,
  idPrefix,
}: {
  draft: RowDraft;
  onChange: (next: RowDraft) => void;
  nodes: readonly AccessNodePublic[];
  applications: readonly ApplicationPublic[];
  disabled?: boolean;
  idPrefix: string;
}) {
  const appSelectId = `${idPrefix}-app`;
  const nameId = `${idPrefix}-name`;
  const place = nodes.find((node) => node.id === draft.nodeId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <span className="tx-muted" style={fieldLabelStyle}>
          {STR.access.catalog.filedUnderLabel}
        </span>
        <FiledUnderPicker
          nodes={nodes}
          value={draft.nodeId}
          disabled={disabled}
          idPrefix={idPrefix}
          onChange={(nodeId) => {
            onChange({ ...draft, nodeId });
          }}
        />
        <span className="tx-subtle" style={hintStyle}>
          {STR.access.catalog.filedUnderHint}{' '}
          {STR.access.catalog.filedUnderNow(
            place === undefined
              ? STR.accessCommon.notPlaced
              : place.path.map((step) => step.name).join(STR.access.catalog.levelArrow),
          )}
        </span>
      </div>

      <label htmlFor={nameId} style={{ display: 'block' }}>
        <span className="tx-muted" style={fieldLabelStyle}>
          {STR.access.catalog.rowNameLabel}
        </span>
        <input
          id={nameId}
          className="field"
          type="text"
          value={draft.name}
          maxLength={200}
          disabled={disabled}
          placeholder={STR.access.catalog.rowNamePlaceholder}
          onChange={(event) => {
            onChange({ ...draft, name: event.target.value });
          }}
          style={textFieldStyle}
        />
        <span className="tx-subtle" style={hintStyle}>
          {STR.access.catalog.rowNameHint}
        </span>
      </label>

      <label htmlFor={appSelectId} style={{ display: 'block' }}>
        <span className="tx-muted" style={fieldLabelStyle}>
          {STR.access.catalog.appLabel}
        </span>
        <select
          id={appSelectId}
          className="field"
          value={draft.applicationId ?? ''}
          disabled={disabled}
          onChange={(event) => {
            const next = event.target.value;
            onChange({ ...draft, applicationId: next === '' ? null : next });
          }}
          style={textFieldStyle}
        >
          <option value="">{STR.accessCommon.outsideSystem}</option>
          {applications.map((application) => (
            <option key={application.id} value={application.id}>
              {application.name}
            </option>
          ))}
        </select>
        <span className="tx-subtle" style={hintStyle}>
          {STR.access.catalog.appHint}
        </span>
      </label>

      <label style={disabled ? { ...checkboxRowStyle, cursor: 'not-allowed' } : checkboxRowStyle}>
        <input
          type="checkbox"
          checked={draft.requestable}
          disabled={disabled}
          onChange={(event) => {
            onChange({ ...draft, requestable: event.target.checked });
          }}
          style={checkboxStyle}
        />
        <span>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 650 }}>
            {STR.access.catalog.requestableTitle}
          </span>
          <span className="tx-subtle" style={{ ...hintStyle, marginTop: 2, fontSize: 12 }}>
            {STR.access.catalog.requestableNote}
          </span>
        </span>
      </label>

      <label htmlFor={`${idPrefix}-approver`} style={{ display: 'block' }}>
        <span className="tx-muted" style={fieldLabelStyle}>
          {STR.access.catalog.approverLabel}
        </span>
        <select
          id={`${idPrefix}-approver`}
          className="field"
          value="admin"
          disabled
          style={textFieldStyle}
        >
          <option value="admin">{STR.access.catalog.approverAdmins}</option>
        </select>
        <span className="tx-subtle" style={hintStyle}>
          {STR.access.catalog.approverHint}
        </span>
      </label>

      <label style={disabled ? { ...checkboxRowStyle, cursor: 'not-allowed' } : checkboxRowStyle}>
        <input
          type="checkbox"
          checked={draft.hrCanGrant}
          disabled={disabled}
          onChange={(event) => {
            onChange({ ...draft, hrCanGrant: event.target.checked });
          }}
          style={checkboxStyle}
        />
        <span>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 650 }}>
            {STR.access.catalog.hrTitle}
          </span>
          <span className="tx-subtle" style={{ ...hintStyle, marginTop: 2, fontSize: 12 }}>
            {STR.access.catalog.hrNote}
          </span>
        </span>
      </label>
    </div>
  );
}
